/**
 * © 2026 iTech India Private Limited. All rights reserved.
 * Licensed under the iTech End User License Agreement (EULA).
 * Unauthorized use or distribution is prohibited.
 */

import { observer } from 'mobx-react-lite';
import { useEffect, type JSX } from 'react';
import { Navigate, Outlet } from 'react-router-dom';

import { authStore } from '../lib/stores/auth-store.js';

/**
 * Attempts a cookie-based session refresh exactly once on first mount, then
 * renders children (or redirects). Keeps a hard page reload from logging the
 * user out even though the access token only lives in memory.
 */
function useSessionBootstrap(): boolean {
  useEffect(() => {
    if (authStore.initializing) {
      void authStore.refresh();
    }
  }, []);
  return authStore.initializing;
}

/** Guards customer portals — requires a customer (or impersonation) session. */
export const RequireCustomerAuth = observer(function RequireCustomerAuth(): JSX.Element {
  const initializing = useSessionBootstrap();
  if (initializing) return <FullPageSpinner />;
  if (!authStore.isAuthenticated || authStore.isStaff) return <Navigate to="/" replace />;
  return <Outlet />;
});

/** Guards the staff console — requires a staff session, never customer. */
export const RequireStaffAuth = observer(function RequireStaffAuth(): JSX.Element {
  const initializing = useSessionBootstrap();
  if (initializing) return <FullPageSpinner />;
  if (!authStore.isAuthenticated || !authStore.isStaff) {
    return <Navigate to="/staff/login" replace />;
  }
  return <Outlet />;
});

function FullPageSpinner(): JSX.Element {
  return (
    <div className="flex h-screen items-center justify-center" role="status" aria-live="polite">
      <div className="border-brand-600 h-8 w-8 animate-spin rounded-full border-2 border-t-transparent" />
      <span className="sr-only">Loading…</span>
    </div>
  );
}
